import { platformClientVersionAtLeast, platformRuntimeBootstrapSchema, platformSemanticVersionSchema, } from '@crest/platform-contracts/runtime';
export class PlatformRuntimeBootstrapSupersededError extends Error {
    constructor() {
        super('Runtime bootstrap request was superseded by a session transition.');
        this.name = 'PlatformRuntimeBootstrapSupersededError';
    }
}
export class PlatformWebClientUpgradeRequiredError extends Error {
    currentVersion;
    minimumVersion;
    constructor(currentVersion, minimumVersion) {
        super('This CREST web client must be updated before it can load tenant data.');
        this.currentVersion = currentVersion;
        this.minimumVersion = minimumVersion;
        this.name = 'PlatformWebClientUpgradeRequiredError';
    }
}
export class PlatformReleaseCatalogMismatchError extends Error {
    constructor() {
        super('This CREST client does not match the active server release catalog.');
        this.name = 'PlatformReleaseCatalogMismatchError';
    }
}
function accessPartitionKey(data) {
    return JSON.stringify([
        data.tenant.id,
        data.actor.userId,
        data.membership.id,
        data.revisions.session,
        data.revisions.access,
        data.deployment.activationEpoch,
        data.deployment.releaseCatalogHash,
        data.configuration.releaseId,
        data.configuration.revision,
    ]);
}
function clone(data) {
    return structuredClone(data);
}
export class PlatformRuntimeBootstrapClient {
    api;
    webClientVersion;
    releaseCatalogHash;
    #current;
    #requestRevision = 0;
    constructor(api, webClientVersion, releaseCatalogHash) {
        this.api = api;
        this.webClientVersion = webClientVersion;
        this.releaseCatalogHash = releaseCatalogHash;
        platformSemanticVersionSchema.parse(webClientVersion);
        if (!/^[a-f0-9]{64}$/.test(releaseCatalogHash)) {
            throw new Error('Web release catalog hash must be a SHA-256 digest.');
        }
    }
    async load(signal) {
        const requestRevision = ++this.#requestRevision;
        const prior = this.#current;
        let response;
        try {
            response = await this.api.requestConditional('/api/v1/runtime/bootstrap', platformRuntimeBootstrapSchema, {
                ...(prior?.entityTag === null || prior?.entityTag === undefined
                    ? {}
                    : { entityTag: prior.entityTag }),
                ...(signal === undefined ? {} : { init: { signal } }),
            });
        }
        catch (error) {
            if (requestRevision !== this.#requestRevision) {
                throw new PlatformRuntimeBootstrapSupersededError();
            }
            throw error;
        }
        if (requestRevision !== this.#requestRevision) {
            throw new PlatformRuntimeBootstrapSupersededError();
        }
        if (response.status === 'not-modified') {
            if (prior === undefined || prior.entityTag !== response.entityTag) {
                throw new Error('Runtime bootstrap returned 304 without the matching current representation.');
            }
            return {
                data: clone(prior.data),
                source: 'not-modified',
                accessPartitionChanged: false,
            };
        }
        const data = response.value.data;
        if (data.deployment.releaseCatalogHash !== this.releaseCatalogHash) {
            throw new PlatformReleaseCatalogMismatchError();
        }
        if (!platformClientVersionAtLeast(this.webClientVersion, data.deployment.minimumClientVersions.web)) {
            throw new PlatformWebClientUpgradeRequiredError(this.webClientVersion, data.deployment.minimumClientVersions.web);
        }
        const nextAccessPartitionKey = accessPartitionKey(data);
        const accessPartitionChanged = prior !== undefined &&
            prior.accessPartitionKey !== nextAccessPartitionKey;
        this.#current = {
            data: clone(data),
            entityTag: response.entityTag,
            accessPartitionKey: nextAccessPartitionKey,
        };
        return { data: clone(data), source: 'network', accessPartitionChanged };
    }
    clear() {
        this.#requestRevision += 1;
        this.#current = undefined;
    }
    current() {
        return this.#current === undefined ? null : clone(this.#current.data);
    }
}
//# sourceMappingURL=platform-runtime-bootstrap-client.js.map